import React, { useMemo, useState } from 'react';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { waitForMs } from 'libs/end-to-end/src/generic';
import { PollVisualizerResponse } from 'libs/end-to-end/src/visualization';
import { GenerateVisualizationEvent } from 'libs/end-to-end/src/visualizer';
import { Tooltip } from '@mui/material';
import { IconShareSvg } from 'apps/sigma2-ui/src/components/svg/IconShare';
import { IconDownloadSvg } from 'apps/sigma2-ui/src/components/svg/IconDownload';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { getDateAsYYYYMMDD } from 'libs/end-to-end/src/dates';

const generateFileName = ({
  organizationName,
  event,
  date,
}: {
  organizationName: string;
  event: GenerateVisualizationEvent;
  date: Date;
}): string => {
  let fileName = `${organizationName}_`;
  if (event.eventType === 'hiringCopilot') {
    fileName += 'hiring_copilot_';
  } else if (event.eventType === 'promotionCopilot') {
    fileName += 'promotion_copilot_';
  } else {
    throw guardExhaustive(event);
  }

  // Get current date in YYYY-MM-DD format
  const dateAsString = getDateAsYYYYMMDD(date);
  fileName += dateAsString;

  return fileName;
};

interface SlateActionsViewProps {
  downloadableGenerateVisualizationEvent: GenerateVisualizationEvent | null;
  isFetchingShareLink: boolean;
  onFetchShareLink: () => void;
  isShareable: boolean;
}

export const SlateActionsView = (props: SlateActionsViewProps) => {
  const { isFetchingShareLink, onFetchShareLink, isShareable } = props;
  const { myUser, userAuthenticatedApiCall } = useUser();

  const organizationName = useMemo(() => {
    return myUser?.organization?.name ?? '';
  }, [myUser]);

  const [genericErrorMessage, setGenericErrorMessage] = useState<string | null>(
    null,
  );

  const [isDownloadingVisualization, setIsDownloadingVisualization] =
    useState(false);

  const onDownloadVisualization = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        isDownloadingVisualization ||
        !props.downloadableGenerateVisualizationEvent
      ) {
        return;
      }
      setIsDownloadingVisualization(true);

      const event = props.downloadableGenerateVisualizationEvent;

      const generateVisualizationResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/visualizer/generate`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(event),
        },
        responseSchema: GenericSuccess,
      });
      if (
        !generateVisualizationResult ||
        generateVisualizationResult.resultType === 'err' ||
        !generateVisualizationResult.value.success
      ) {
        setGenericErrorMessage(
          'Error generating visualization. Please try again.',
        );
        setIsDownloadingVisualization(false);
        return;
      }

      while (isAlive()) {
        await waitForMs(3000);
        const pollVisualizationResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/visualizer/poll`,
          requestOptions: {
            method: 'PUT',
            body: JSON.stringify(event),
          },
          responseSchema: PollVisualizerResponse,
        });

        if (
          !pollVisualizationResult ||
          pollVisualizationResult.resultType === 'err' ||
          pollVisualizationResult.value.visualizationStatus === 'Failed'
        ) {
          setGenericErrorMessage(
            'Error generating visualization. Please try again.',
          );
          setIsDownloadingVisualization(false);
          return;
        }

        if (
          pollVisualizationResult.value.visualizationStatus === 'Complete' &&
          pollVisualizationResult.value.link
        ) {
          try {
            const fetchResult = await fetch(
              pollVisualizationResult.value.link,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/zip',
                },
              },
            );
            if (!fetchResult.ok) {
              setGenericErrorMessage(
                'Error downloading visualization. Please try again.',
              );
              setIsDownloadingVisualization(false);
              return;
            }
            const blobResult = await fetchResult.blob();

            const file = new Blob([blobResult], { type: 'application/zip' });
            const fileURL = URL.createObjectURL(file);

            const fileLink = document.createElement('a');
            fileLink.href = fileURL;

            fileLink.download = generateFileName({
              organizationName,
              event,
              date: new Date(),
            });

            // triggers the click event
            fileLink.click();
            fileLink.remove();
            setIsDownloadingVisualization(false);
            return;
          } catch {
            setGenericErrorMessage(
              'Error downloading visualization. Please try again.',
            );
            setIsDownloadingVisualization(false);
            return;
          }
        }
      }

      setIsDownloadingVisualization(false);
      return;
    },
    [
      isDownloadingVisualization,
      organizationName,
      props,
      userAuthenticatedApiCall,
    ],
  );

  return (
    <div className="bg-white">
      <div className="flex items-center gap-2">
        <button
          disabled={isFetchingShareLink || !isShareable}
          onClick={() => onFetchShareLink()}
        >
          <Tooltip placement="bottom" title="Share Link">
            <span aria-label="Share Icon" data-qa="share-button">
              <IconShareSvg svg={{ className: 'text-secondary' }} />
            </span>
          </Tooltip>
        </button>

        <button
          disabled={isDownloadingVisualization || !isShareable}
          onClick={() => onDownloadVisualization()}
        >
          <Tooltip placement="bottom" title="Download">
            <span aria-label="Download Icon" data-qa="download-button">
              <IconDownloadSvg svg={{ className: 'text-secondary' }} />
            </span>
          </Tooltip>
        </button>
      </div>
      {genericErrorMessage && (
        <div className="flex p-1 items-center">
          <Paragraph textColor="text-red-600">{genericErrorMessage}</Paragraph>
        </div>
      )}
      {!genericErrorMessage && <div></div>}
    </div>
  );
};
